import React, { useEffect } from 'react';
import {
  TextField,
  Typography,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useFormContext, useWatch, useFormState } from 'react-hook-form';
import { useWhat3WordsValidation } from '../../../../hooks/useWhat3WordsValidation';
import { SiteImportFormData } from '../../../../schemas/siteImportSchema';

const What3WordsField: React.FC = () => {
  const form = useFormContext<SiteImportFormData>();
  const { control, setValue } = form;
  const { errors } = useFormState({ control });

  const what3words = useWatch({ control, name: 'what3words' }) ?? '';

  const { validateAddress, isChecking: w3wChecking } =
    useWhat3WordsValidation(form);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.replace(/^\/\/\//, '');
    setValue('what3words', value, { shouldValidate: true, shouldDirty: true });
  };

  useEffect(() => {
    if (what3words && /^[a-zA-Z]+\.[a-zA-Z]+\.[a-zA-Z]+$/.test(what3words)) {
      validateAddress(what3words);
    }
  }, [what3words, validateAddress]);

  const getAdornment = () => {
    if (!what3words) return null;
    if (w3wChecking) return <CircularProgress size={20} />;
    if (errors.what3words) return <ErrorOutlineIcon color="error" />;
    if (!w3wChecking) return <CheckCircleOutlineIcon color="success" />;
    return null;
  };

  const getHelperText = () => {
    if (!what3words) {
      return 'Optional - Three words separated by dots (e.g., filled.count.soap)';
    }
    if (errors.what3words) return errors.what3words.message;
    if (w3wChecking) return 'Checking address...';
    const lat = form.getValues('latitude');
    const lng = form.getValues('longitude');
    if (lat != null && lng != null) {
      return `Valid (${lat.toFixed(6)}, ${lng.toFixed(6)})`;
    }
    return 'Valid address';
  };

  return (
    <TextField
      label="What3Words Address"
      value={what3words}
      onChange={handleChange}
      fullWidth
      placeholder="filled.count.soap"
      helperText={getHelperText()}
      error={Boolean(errors.what3words)}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <Typography
                sx={{ fontFamily: 'monospace', color: 'text.secondary' }}
              >
                {'///'}
              </Typography>
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">{getAdornment()}</InputAdornment>
          ),
        },
      }}
      inputProps={{ 'data-testid': 'site-what3words-input' }}
    />
  );
};

export default What3WordsField;
